<template>
  <!-- Authentication Screen -->
  <Auth v-if="!isAuthenticated" @auth-success="handleAuthSuccess" />
  
  <!-- Main Application (only shown when authenticated) -->
  <div v-else class="main-app-wrapper">
    <div class="container-fluid">
      <!-- Header -->
      <div class="row">
        <div class="col-12 mb-4 denoise-header">
          <div class="d-flex justify-content-between align-items-center py-3">
            <h1 class="h3 mb-0">
              <i class="fas fa-camera text-primary"></i>
              AutoDenoise
            </h1>
            <div class="d-flex align-items-center">
              <span class="text-muted me-3 d-none d-md-inline">Welcome, {{ currentUser?.username }}</span>
              <Button 
                @click="handleLogout"
                icon="pi pi-sign-out"
                label="Logout"
                severity="secondary"
                size="small"
              />
            </div>
          </div>
        </div>
      </div>
      
      <!-- Messages -->
      <div class="row" v-if="error || success">
        <div class="col-12">
          <div class="alert alert-danger" v-if="error">
            <i class="fas fa-exclamation-triangle"></i>
            {{ error }}
            <button @click="clearMessages" class="btn-close float-end"></button>
          </div>
          <div class="alert alert-success" v-if="success">
            <i class="fas fa-check-circle"></i>
            {{ success }}
            <button @click="clearMessages" class="btn-close float-end"></button>
          </div>
        </div>
      </div>
      
      <!-- Main Content -->
      <div class="content-padding">
        <div class="row">
          <!-- File Tree -->
          <div class="col-md-8 mb-4">
            <FileTree
              ref="fileTree"
              :config="config"
              :loading="loading"
              :selectedFiles="selectedFiles"
              :isProcessing="queueStatus && queueStatus.processing"
              @file-selected="onFileSelected"
              @file-deselected="onFileDeselected"
              @error="onError"
            />
          </div>
          
          <!-- Sidebar -->
          <div class="col-md-4">
            <!-- Selected Files -->
            <SelectedFiles
              :selectedFiles="selectedFiles"
              :loading="processingLoading"
              @remove-file="onFileDeselected"
              @process-files="processSelectedFiles"
            />
            
            <!-- Queue Status -->
            <QueueStatus class="mb-4" :queueStatus="queueStatus" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Auth from './components/Auth.vue'
import FileTree from './components/FileTreePrimeVue.vue'
import SelectedFiles from './components/SelectedFiles.vue'
import QueueStatus from './components/QueueStatus.vue'

export default {
  name: 'App',
  components: {
    Auth,
    FileTree,
    SelectedFiles,
    QueueStatus
  },
  data() {
    return {
      apiBaseUrl: this.getApiBaseUrl(),
      config: null,
      selectedFiles: [],
      queueStatus: null,
      loading: false,
      processingLoading: false,
      error: null,
      success: null,
      previousCompletedJobs: new Set(), // Track completed jobs to detect new completions
      isAuthenticated: false,
      currentUser: null,
      authToken: null,
      queuePollingInterval: null // Track the polling interval
    }
  },
  
  async mounted() {
    // Check for existing authentication
    await this.checkExistingAuth()
    
    // Only load config and start polling if authenticated
    if (this.isAuthenticated) {
      await this.loadConfig()
      this.startQueuePolling()
    }
  },

  beforeUnmount() {
    // Clean up polling interval when component is destroyed
    this.stopQueuePolling()
  },
  
  methods: {
    async checkExistingAuth() {
      const token = localStorage.getItem('authToken')
      const user = localStorage.getItem('user')
      
      if (token && user) {
        try {
          // Verify token is still valid
          const response = await axios.get(`${this.apiBaseUrl}/auth/verify`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
          
          if (response.data.valid) {
            this.authToken = token
            this.currentUser = JSON.parse(user)
            this.isAuthenticated = true
            this.setupAxiosAuth()
          } else {
            this.clearAuth()
          }
        } catch (error) {
          console.error('Token verification failed:', error)
          this.clearAuth()
        }
      }
    },

    handleAuthSuccess(authData) {
      this.authToken = authData.token
      this.currentUser = authData.user
      this.isAuthenticated = true
      this.setupAxiosAuth()
      
      // Load config and start polling now that we're authenticated
      this.loadConfig()
      this.startQueuePolling()
    },

    setupAxiosAuth() {
      // Set default Authorization header for all axios requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${this.authToken}`
    },

    handleLogout() {
      this.clearAuth()
      // Optionally call logout endpoint
      axios.post(`${this.apiBaseUrl}/auth/logout`).catch(() => {
        // Ignore errors on logout
      })
    },

    clearAuth() {
      // Stop queue polling
      this.stopQueuePolling()
      
      this.authToken = null
      this.currentUser = null
      this.isAuthenticated = false
      this.selectedFiles = []
      this.queueStatus = null
      
      // Clear localStorage
      localStorage.removeItem('authToken')
      localStorage.removeItem('user')
      
      // Remove axios auth header
      delete axios.defaults.headers.common['Authorization']
    },

    getApiBaseUrl() {
      // For production, use the deployed API endpoint with /api prefix
      if (process.env.NODE_ENV === 'production') {
        return `${window.location.protocol}//${window.location.hostname}/api`
      }
      
      // For development, always use localhost:3000 (backend server) with /api prefix
      const protocol = window.location.protocol
      const hostname = window.location.hostname
      const port = '3000'
      
      // If accessing via localhost, keep localhost (for development on same machine)
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `${protocol}//localhost:${port}/api`
      }
      
      // For mobile or other devices, use the actual IP address with port 3000
      return `${protocol}//${hostname}:${port}/api`
    },

    async loadConfig() {
      try {
        const response = await axios.get(`${this.apiBaseUrl}/config`)
        this.config = response.data
      } catch (error) {
        this.error = 'Failed to load configuration: ' + error.message
      }
    },
    
    async loadQueueStatus() {
      try {
        const response = await axios.get(`${this.apiBaseUrl}/queue/status`)
        const newQueueStatus = response.data
        
        // Check for newly completed jobs
        if (newQueueStatus.jobs) {
          const currentCompletedJobs = new Set()
          let hasNewCompletions = false
          let newlyCompletedJobs = []
          
          // Find completed jobs
          newQueueStatus.jobs.forEach(job => {
            if (job.status === 'completed') {
              currentCompletedJobs.add(job.id)
              // If this job wasn't completed before, it's a new completion
              if (!this.previousCompletedJobs.has(job.id)) {
                hasNewCompletions = true
                newlyCompletedJobs.push(job.id)
              }
            }
          })
          
          // If we have new completions, refresh the file tree
          if (hasNewCompletions) {
            setTimeout(() => {
              this.refreshFileTreePreservingState()
            }, 2000) // Wait 2 seconds to ensure DXO has finished writing files
          }
          
          // Update the tracking set
          this.previousCompletedJobs = currentCompletedJobs
        }
        
        this.queueStatus = newQueueStatus
      } catch (error) {
        console.error('Failed to load queue status:', error)
      }
    },
    
    startQueuePolling() {
      // Clear any existing interval first
      this.stopQueuePolling()
      
      // Start immediate polling
      this.loadQueueStatus()
      
      // Set up interval and store the ID
      this.queuePollingInterval = setInterval(() => {
        this.loadQueueStatus()
      }, 5000) // Poll every 5 seconds
    },

    stopQueuePolling() {
      if (this.queuePollingInterval) {
        clearInterval(this.queuePollingInterval)
        this.queuePollingInterval = null
      }
    },
    
    onFileSelected(file) {
      const index = this.selectedFiles.findIndex(f => f.path === file.path)
      if (index === -1) {
        this.selectedFiles.push(file)
      }
    },
    
    onFileDeselected(file) {
      const index = this.selectedFiles.findIndex(f => f.path === file.path)
      if (index > -1) {
        this.selectedFiles.splice(index, 1)
      }
    },
    
    async processSelectedFiles() {
      if (this.selectedFiles.length === 0) {
        this.error = 'Please select at least one RAW file'
        return
      }
      
      try {
        this.processingLoading = true
        this.error = null
        
        const imagePaths = this.selectedFiles.map(file => file.path)
        const response = await axios.post(`${this.apiBaseUrl}/process-images`, {
          imagePaths: imagePaths
        })
        
        this.success = `Job ${response.data.jobId} added to queue with ${imagePaths.length} RAW files`
        this.selectedFiles = []
        
        // Refresh queue status
        await this.loadQueueStatus()
        
      } catch (error) {
        const errorData = error.response?.data
        const errorMessage = errorData?.message || error.message
        
        // Check if error is about all files already being processed
        if (errorData?.error === 'No files to process' && 
            errorMessage.includes('skipped') && 
            errorMessage.includes('already processed')) {
          
          // Clear all selected files since they're all already processed
          this.selectedFiles = []
          this.error = errorMessage + ' - All files have been deselected.'
        } else {
          this.error = 'Failed to process RAW files: ' + (errorData?.error || error.message)
        }
      } finally {
        this.processingLoading = false
      }
    },
    
    onError(errorMessage) {
      this.error = errorMessage
    },
    
    clearMessages() {
      this.error = null
      this.success = null
    },
    
    refreshFileTreePreservingState() {
      // Tell the FileTree component to refresh while preserving expanded folders
      if (this.$refs.fileTree) {
        this.$refs.fileTree.refreshPreservingState()
      }
    }
  }
}
</script>

<style scoped>
.main-app-wrapper {
  min-height: 100vh;
}

.denoise-header {
  background: white;
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}

.content-padding {
  padding-left: 1.5rem;
  padding-right: 1.5rem;
}
</style>